import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type {
    ItemCollection,
} from '@toolproof-core/visualization/_contract/collection';
import type { ItemKey } from '@toolproof-core/visualization/_contract/key';

import {
    buildToolItemKey,
    buildWorkflowStateEntryConstellationKey,
    parseWorkflowStateEntryCollectionKey,
    parseWorkflowStateEntryItemKey,
} from '@/workflow/_pure/keyCodec';
import { parseToolStepKeyFromItemKey as parseSharedToolStepKeyFromItemKey } from '@/workflow/_pure/parsing';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export function parseToolHandleFromItemKey(itemKey: string): ToolHandle | null {
    const keyPrefix = 'item:entity:';
    if (!itemKey.startsWith(keyPrefix)) return null;

    const toolHandle = decodeURIComponent(itemKey.slice(keyPrefix.length));
    if (!toolHandle.startsWith('TOOL-')) return null;
    return toolHandle.length > 0 ? toolHandle as ToolHandle : null;
}

export function parseToolStepKeyFromItemKey(itemKey: string): string | null {
    return parseSharedToolStepKeyFromItemKey(itemKey as ItemKey<'entity'>);
}

export function deriveSelectedToolItemKey(runtimeState: RuntimeState): ItemKey<'entity'> | undefined {
    const selectedToolHandle = runtimeState.selectionState.selectedToolStep?.toolHandle;
    if (!selectedToolHandle) return undefined;
    return buildToolItemKey(selectedToolHandle);
}

export function isRuntimeState(runtimeState: unknown): runtimeState is RuntimeState {
    const candidate = runtimeState as Partial<RuntimeState> | null | undefined;
    return Boolean(candidate && candidate.adapterMode === 'builder' && typeof candidate.phase === 'string' && candidate.mutableWorkflow);
}

export function deriveSelectedInputWorkflowStateEntryActiveRef(args: {
    itemCollections: ItemCollection[];
    selectedStepKey: string;
    selectedRoleName: string;
}): {
    constellationKey: string;
    itemKey: ItemKey<'entity'>;
} | null {
    for (const itemCollection of args.itemCollections) {
        if (itemCollection.itemCollectionKind !== 'entity') continue;

        const parsedCollection = parseWorkflowStateEntryCollectionKey(itemCollection.itemCollectionKey);
        if (!parsedCollection || parsedCollection.direction !== 'input') continue;

        for (const item of itemCollection.items) {
            const parsedItem = parseWorkflowStateEntryItemKey(item.itemKey);
            if (!parsedItem) continue;
            if (parsedItem.direction !== 'input') continue;
            if (parsedItem.stepKey !== args.selectedStepKey) continue;
            if (parsedItem.roleName !== args.selectedRoleName) continue;

            return {
                constellationKey: buildWorkflowStateEntryConstellationKey({
                    workflowHandle: parsedCollection.workflowHandle,
                    threadIndex: parsedCollection.threadIndex,
                    stepKey: parsedCollection.stepKey,
                    resourceTypeHandle: parsedCollection.resourceTypeHandle,
                    direction: parsedCollection.direction,
                }),
                itemKey: item.itemKey,
            };
        }
    }

    return null;
}
